/**
 * BTC · Phase 6 — Ask Northstar (frontend)
 *
 * Locked scope:
 *  - single-turn, closed-world, BTC-grounded assistant
 *  - never sends `user_id` / `patient_id` / `member_id`
 *  - shows deterministic crisis card / medical-refusal card / grounded
 *    answer + BTC sources
 *  - no chat history persistence (R6, R7)
 *  - no streaming, no persistence, no analytics beyond the audit that
 *    the server already writes
 */
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Heart, Send, Sparkles } from "lucide-react";
import { useActivation } from "../../hooks/useActivation";
import { api } from "../../lib/auth";
import DeepSpaceBackground from "../../components/northstar/DeepSpaceBackground";

/**
 * Minimum inline Markdown renderer for Ask Northstar answers.
 *
 * The provider (Claude Sonnet 4-6) emits three lightweight Markdown
 * patterns in practice: `**bold**`, `- ` list items, and blank-line
 * paragraph breaks. Rendering `response.body` as plain text inside a
 * `whitespace-pre-wrap` block produced literal `**` and `-` characters
 * for real users (verified via DOM: `has_asterisk_literal=true`,
 * `has_strong=0`, `has_li=0`).
 *
 * This helper renders only those three patterns. It uses ordinary React
 * children, so all text is HTML-escaped by React — no
 * `dangerouslySetInnerHTML`, no third-party parser, no new package. It
 * intentionally does NOT render links, images, headings, tables, or raw
 * HTML, keeping R4 (no external web / no embedded HTML) and the XSS
 * boundary intact.
 */
function renderInlineBold(text) {
  const parts = String(text ?? "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**") && p.length > 4) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

function renderBtcMarkdown(body) {
  if (!body) return null;
  const blocks = String(body).split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split("\n");
    const isList = lines.every((l) => /^\s*-\s+/.test(l));
    if (isList) {
      return (
        <ul key={bi} className="list-disc pl-5 space-y-1">
          {lines.map((l, li) => (
            <li key={li}>{renderInlineBold(l.replace(/^\s*-\s+/, ""))}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={bi} className="whitespace-pre-wrap">
        {renderInlineBold(block)}
      </p>
    );
  });
}

export default function AskNorthstar() {
  const { data: activation, loading } = useActivation();
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#040816]"
        data-testid="ask-northstar-loading"
      >
        <Heart className="w-8 h-8 text-[#7ED493] animate-pulse" />
      </div>
    );
  }
  if (!activation) return <Navigate to="/portal" replace />;
  if (!activation.northstar_access_granted) {
    return <Navigate to="/northstar" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    setResponse(null);
    try {
      const r = await api.post("/northstar/ask", { question: q });
      setResponse(r.data);
    } catch (e) {
      if (e?.response?.status === 429) {
        setError({
          type: "rate_limited",
          message: "You've reached the Ask Northstar limit. Please try again later.",
        });
      } else if (e?.response?.status === 413) {
        setError({ type: "too_long", message: "That question is too long. Please shorten it." });
      } else {
        setError({
          type: "unknown",
          message: "Ask Northstar is temporarily unavailable. Please try again shortly.",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden bg-[#040816]"
      data-testid="ask-northstar"
    >
      <DeepSpaceBackground />

      <header className="relative z-20 px-4 sm:px-6 pt-4 sm:pt-5 pb-2">
        <div className="flex items-center justify-between">
          <p
            className="text-[11px] uppercase tracking-[0.28em] text-[#7ED493] font-semibold"
            data-testid="ask-brand"
          >
            Ask Northstar
          </p>
          <Link
            to="/northstar"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors py-1.5 px-2 -mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493] rounded"
            data-testid="ask-back-to-shell"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Northstar
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 pb-24 pt-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
          Ask Northstar
        </h1>
        <p className="text-sm text-white/70 mb-1 max-w-lg">
          A grounded assistant that shares general educational and supportive
          information from BTC-approved sources.
        </p>
        <p
          className="text-xs text-white/50 mb-6 max-w-lg"
          data-testid="ask-disclaimer"
        >
          Ask Northstar is not a clinician and does not provide individualized
          medical guidance. For medical questions, please talk to your care team.
        </p>

        <form onSubmit={submit} className="mb-6" data-testid="ask-form">
          <label htmlFor="ask-input" className="sr-only">
            Your question
          </label>
          <div className="flex gap-2 items-start">
            <textarea
              id="ask-input"
              data-testid="ask-input"
              className="flex-1 rounded-xl bg-white/[0.06] border border-white/15 focus:border-[#7ED493] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493]/40 px-4 py-3 text-sm text-white placeholder:text-white/40 min-h-[96px] resize-y"
              placeholder="Ask about BTC wellness, community, or how BTC support works…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={4096}
              disabled={busy}
            />
            <button
              type="submit"
              data-testid="ask-submit"
              disabled={busy || !question.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#7ED493] text-[#0B132B] px-4 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493] focus-visible:ring-offset-2 focus-visible:ring-offset-[#040816]"
              aria-label="Send question"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{busy ? "Asking…" : "Ask"}</span>
            </button>
          </div>
        </form>

        {error && (
          <div
            className="rounded-xl border border-white/20 bg-white/[0.04] p-4 mb-4 text-sm"
            data-testid="ask-error"
          >
            {error.message}
          </div>
        )}

        {response?.response_type === "crisis" && (
          <div
            className="rounded-2xl border-2 border-[#FFB0A9] bg-[#2A0B08]/60 p-5 sm:p-6 mb-4"
            data-testid="ask-crisis-card"
            role="alert"
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle
                className="w-5 h-5 text-[#FFB0A9] flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <h2 className="text-lg font-bold mb-1">{response.headline}</h2>
                <p className="text-sm text-white/85">{response.body}</p>
              </div>
            </div>
            <ul className="space-y-2 mb-4" data-testid="ask-crisis-resources">
              {response.resources.map((r, i) => (
                <li key={i} className="text-sm">
                  <span className="font-semibold text-[#FFB0A9]">{r.label}</span>
                  <span className="text-white/75"> — {r.detail}</span>
                </li>
              ))}
            </ul>
            {response.btc_support_pathway && (
              <div
                className="border-t border-white/15 pt-3"
                data-testid="ask-crisis-btc-pathway"
              >
                <p className="text-xs uppercase tracking-wider text-white/60 mb-1">
                  {response.btc_support_pathway.label}
                </p>
                <p className="text-sm text-white/80 mb-2">
                  {response.btc_support_pathway.detail}
                </p>
                <Link
                  to="/support"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0B132B] px-4 py-2 text-xs font-semibold hover:bg-[#7ED493] transition-colors"
                  data-testid="ask-crisis-btc-pathway-link"
                >
                  Open the BTC support pathway
                </Link>
              </div>
            )}
          </div>
        )}

        {response?.response_type === "medical_refusal" && (
          <div
            className="rounded-2xl border border-[#7ED493]/40 bg-white/[0.04] p-5 mb-4"
            data-testid="ask-medical-refusal-card"
          >
            <div className="flex items-start gap-3">
              <Sparkles
                className="w-5 h-5 text-[#7ED493] flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <h2 className="text-lg font-bold mb-1">{response.headline}</h2>
                <p className="text-sm text-white/85">{response.body}</p>
              </div>
            </div>
          </div>
        )}

        {response?.response_type === "answer" && (
          <div
            className="rounded-2xl border border-white/15 bg-white/[0.04] p-5 mb-4"
            data-testid="ask-answer-card"
          >
            <div
              className="text-sm text-white/90 leading-relaxed space-y-3"
              data-testid="ask-answer-body"
            >
              {renderBtcMarkdown(response.body)}
            </div>
            {response.sources?.length > 0 && (
              <div
                className="mt-4 pt-3 border-t border-white/10"
                data-testid="ask-answer-sources"
              >
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#7ED493]/80 mb-1">
                  Sources
                </p>
                <ul className="text-xs text-white/70 space-y-0.5">
                  {response.sources.map((s) => (
                    <li key={s.id} data-source-id={s.id}>
                      • {s.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {response?.response_type === "unavailable" && (
          <div
            className="rounded-2xl border border-white/20 bg-white/[0.04] p-5 mb-4"
            data-testid="ask-unavailable-card"
          >
            <h2 className="text-lg font-bold mb-1">{response.headline}</h2>
            <p className="text-sm text-white/85">{response.body}</p>
          </div>
        )}
      </main>
    </div>
  );
}
